import api from './api';
import {
  Organization,
  OrganizationList,
  StrategicGoal,
  StrategicGoalList,
  Initiative,
  InitiativeList,
  Activity,
  ActivityList,
  KPI,
  KPIList,
  KPIValue,
  KPIReadingSchedule,
  KPIReadingScheduleList,
  Notification,
  NotificationList,
  DashboardData,
  DashboardSettings,
  PerformanceData,
  PaginatedResponse,
  User,
  Service,
  ServiceList,
  OrganizationalUnit,
  OrganizationalUnitList,
  Employee,
  UnitGoal,
  UnitGoalList,
  Process,
  ProcessList,
  SubProcess,
  ProcessStep,
  ChangeRequest,
  ChangeRequestList,
  OrganizationComparison,
  DueKPIReading,
} from '../types';
import { queryClient } from '../providers/QueryProvider';

/**
 * Invalidate every cache affected by a KPI / reading / schedule mutation.
 *
 * Two symptoms this prevents:
 *  - the "قراءات مؤشرات مستحقة" due-readings table serving pre-edit rows
 *    (60s staleTime) after a frequency change ('dueReadings','strategy',
 *    'schedules','kpis');
 *  - the dashboard stat cards (e.g. "مؤشرات الأداء الاستراتيجية") not
 *    reflecting an added/removed KPI, because the stat card reads the
 *    'dashboard' / 'organizations' queries which nothing else invalidates.
 *
 * invalidateQueries matches by key prefix, so the partial keys below cover
 * the parametrised query keys (`['dashboard', userId]`, `['dueReadings',
 * {…}]`, etc.). Fire-and-forget: triggers background refetch of active
 * queries.
 */
const invalidateScheduleCaches = () => {
  ['dueReadings', 'strategy', 'schedules', 'kpis', 'dashboard', 'organizations'].forEach((key) => {
    queryClient.invalidateQueries({ queryKey: [key] });
  });
};

// ============================================================
// Strategy / Executive Dashboard Overview (aggregated across ALL orgs)
// ============================================================
export interface StrategyOverview {
  organizations_count: number;
  all_orgs_active: boolean;

  goals_count: number;
  pending_goals_count: number;
  approved_goals_count: number;

  initiatives_count: number;
  pending_initiatives_count: number;
  approved_initiatives_count: number;

  strategic_kpis_count: number;
  excellent_strategic_kpis: number;
  initiative_kpis_count: number;
  excellent_initiative_kpis: number;
  process_kpis_count: number;
  excellent_process_kpis: number;

  stage_required_progress: number;
  stage_actual_progress: number;
  stage_capacity: number;
  target_percentage: number;
  actual_percentage: number;
  commitment_index: number;

  overall_achievement_rate: number;
  performance_deviation: number | null;
  achievement_improvement: number;
}

// ============================================================
// Organizations API
// ============================================================
export const organizationsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<OrganizationList>>('/organizations/', { params });
    return response.data;
  },

  get: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<Organization>(`/organizations/${id}/`, { params });
    return response.data;
  },

  create: async (data: Partial<Organization>) => {
    const response = await api.post<Organization>('/organizations/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Organization>) => {
    const response = await api.patch<Organization>(`/organizations/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/organizations/${id}/`);
    return response;
  },

  getDashboard: async (id: number, params?: Record<string, any>) => {
    const response = await api.get(`/organizations/${id}/dashboard/`, { params });
    return response.data;
  },

  getKPIs: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<KPIList[]>(`/organizations/${id}/kpis/`, { params });
    return response.data;
  },

  updateVisionMission: async (id: number, data: { vision?: string; mission?: string }) => {
    const response = await api.patch<Organization>(`/organizations/${id}/update_vision_mission/`, data);
    return response.data;
  },

  getComparison: async (params?: { sort_by?: string; order?: 'asc' | 'desc' }) => {
    const response = await api.get<OrganizationComparison[]>('/organizations/comparison/', { params });
    return response.data;
  },

  // Aggregated dashboard overview across ALL organizations (strategy/executive view)
  getOverview: async (params?: { year?: number }) => {
    const response = await api.get<StrategyOverview>('/organizations/overview/', { params });
    return response.data;
  },

  // Get pending approvals for org_executive
  getPendingApprovals: async () => {
    const response = await api.get('/organizations/pending-approvals/');
    return response.data;
  },

  // Batch approve all pending items for a department
  batchApprove: async (departmentId: number) => {
    const response = await api.post(`/organizations/${departmentId}/batch-approve/`);
    return response.data;
  },

  uploadOrgStructure: async (id: number, formData: FormData) => {
    const response = await api.post(`/organizations/${id}/upload-org-structure/`, formData);
    return response.data;
  },

  deleteOrgStructure: async (id: number) => {
    const response = await api.delete(`/organizations/${id}/delete-org-structure/`);
    return response;
  },

  uploadBanner: async (id: number, formData: FormData) => {
    const response = await api.post<{ banner_background: string }>(
      `/organizations/${id}/upload-banner/`,
      formData,
    );
    return response.data;
  },

  uploadLogo: async (id: number, formData: FormData) => {
    const response = await api.patch<Organization>(`/organizations/${id}/`, formData);
    return response.data;
  },

  resetBanner: async (id: number) => {
    const response = await api.post<Organization>(`/organizations/${id}/reset-banner/`);
    return response.data;
  },

  updateBannerTaglines: async (id: number, data: { banner_tagline_ar?: string; banner_tagline_en?: string }) => {
    const response = await api.patch<Organization>(`/organizations/${id}/`, data);
    return response.data;
  },
};

// ============================================================
// Strategic Goals API
// ============================================================
export const goalsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<StrategicGoalList>>('/goals/', { params });
    return response.data;
  },

  get: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<StrategicGoal>(`/goals/${id}/`, { params });
    return response.data;
  },

  create: async (data: Partial<StrategicGoal>) => {
    const response = await api.post<StrategicGoal>('/goals/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<StrategicGoal>) => {
    const response = await api.patch<StrategicGoal>(`/goals/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/goals/${id}/`);
    return response;
  },

  getInitiatives: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<InitiativeList[]>(`/goals/${id}/initiatives/`, { params });
    return response.data;
  },

  getKPIs: async (id: number) => {
    const response = await api.get<KPIList[]>(`/goals/${id}/kpis/`);
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/goals/${id}/deletion-impact/`),

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/goals/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  resultsExport: async (params?: Record<string, any>) => {
    const response = await api.get('/goals/results-export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/goals/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/goals/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================================
// Goal Comments API
// ============================================================
export interface GoalComment {
  id: number;
  strategic_goal: number;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply' | 'approval_note' | 'rejection_reason';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: GoalComment[];
}

export const goalCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<GoalComment[]>('/goal-comments/', { params });
    return response.data;
  },

  getForGoal: async (goalId: number) => {
    const response = await api.get<GoalComment[]>(`/goal-comments/for-goal/${goalId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<GoalComment>(`/goal-comments/${id}/`);
    return response.data;
  },

  create: async (data: { strategic_goal: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<GoalComment>('/goal-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<GoalComment>(`/goal-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/goal-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<GoalComment>(`/goal-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<GoalComment>(`/goal-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<GoalComment[]>('/goal-comments/unread/');
    return response.data;
  },
};

// ============================================================
// KPI Comments API
// ============================================================
export interface KPIComment {
  id: number;
  kpi: number;
  kpi_name?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: KPIComment[];
}

export const kpiCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<KPIComment[]>('/kpi-comments/', { params });
    return response.data;
  },

  getForKPI: async (kpiId: number) => {
    const response = await api.get<KPIComment[]>(`/kpi-comments/for-kpi/${kpiId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPIComment>(`/kpi-comments/${id}/`);
    return response.data;
  },

  create: async (data: { kpi: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<KPIComment>('/kpi-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<KPIComment>(`/kpi-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/kpi-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<KPIComment>(`/kpi-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<KPIComment>(`/kpi-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<KPIComment[]>('/kpi-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Initiative Comments API
// ============================================================
export interface InitiativeComment {
  id: number;
  initiative: number;
  initiative_title?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: InitiativeComment[];
}

export const initiativeCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<InitiativeComment[]>('/initiative-comments/', { params });
    return response.data;
  },

  getForInitiative: async (initiativeId: number) => {
    const response = await api.get<InitiativeComment[]>(`/initiative-comments/for-initiative/${initiativeId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<InitiativeComment>(`/initiative-comments/${id}/`);
    return response.data;
  },

  create: async (data: { initiative: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<InitiativeComment>('/initiative-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<InitiativeComment>(`/initiative-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/initiative-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<InitiativeComment>(`/initiative-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<InitiativeComment>(`/initiative-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<InitiativeComment[]>('/initiative-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Service Comments API
// ============================================================
export interface ServiceComment {
  id: number;
  service: number;
  service_name?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: ServiceComment[];
}

export const serviceCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<ServiceComment[]>('/service-comments/', { params });
    return response.data;
  },

  getForService: async (serviceId: number) => {
    const response = await api.get<ServiceComment[]>(`/service-comments/for-service/${serviceId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<ServiceComment>(`/service-comments/${id}/`);
    return response.data;
  },

  create: async (data: { service: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<ServiceComment>('/service-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<ServiceComment>(`/service-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/service-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<ServiceComment>(`/service-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<ServiceComment>(`/service-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<ServiceComment[]>('/service-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Unit Goal Comments API
// ============================================================
export interface UnitGoalComment {
  id: number;
  unit_goal: number;
  unit_goal_name?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: UnitGoalComment[];
}

export const unitGoalCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<UnitGoalComment[]>('/unit-goal-comments/', { params });
    return response.data;
  },

  getForUnitGoal: async (unitGoalId: number) => {
    const response = await api.get<UnitGoalComment[]>(`/unit-goal-comments/for-unit-goal/${unitGoalId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<UnitGoalComment>(`/unit-goal-comments/${id}/`);
    return response.data;
  },

  create: async (data: { unit_goal: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<UnitGoalComment>('/unit-goal-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<UnitGoalComment>(`/unit-goal-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/unit-goal-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<UnitGoalComment>(`/unit-goal-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<UnitGoalComment>(`/unit-goal-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<UnitGoalComment[]>('/unit-goal-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Initiatives API
// ============================================================
export const initiativesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<InitiativeList>>('/initiatives/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Initiative>(`/initiatives/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Initiative>) => {
    const response = await api.post<Initiative>('/initiatives/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Initiative>) => {
    const response = await api.patch<Initiative>(`/initiatives/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/initiatives/${id}/`);
    return response;
  },

  getDashboard: async (id: number) => {
    const response = await api.get(`/initiatives/${id}/dashboard/`);
    return response.data;
  },

  bulkCreate: async (initiatives: Partial<Initiative>[]) => {
    const response = await api.post<Initiative[]>('/initiatives/bulk_create/', { initiatives });
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/initiatives/${id}/deletion-impact/`),

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/initiatives/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  resultsExport: async (params?: Record<string, any>) => {
    const response = await api.get('/initiatives/results-export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/initiatives/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/initiatives/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================================
// Activities API
// ============================================================
export const activitiesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ActivityList>>('/activities/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Activity>(`/activities/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Activity>) => {
    const response = await api.post<Activity>('/activities/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Activity>) => {
    const response = await api.patch<Activity>(`/activities/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/activities/${id}/`);
    return response;
  },

  // Get activities for a specific initiative
  getForInitiative: async (initiativeId: number) => {
    const response = await api.get<PaginatedResponse<ActivityList>>('/activities/', {
      params: { initiative: initiativeId }
    });
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/activities/${id}/deletion-impact/`),
};

// ============================================================
// KPIs API
// ============================================================
export const kpisApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<KPIList>>('/kpis/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPI>(`/kpis/${id}/`);
    return response.data;
  },

  create: async (data: Partial<KPI>) => {
    const response = await api.post<KPI>('/kpis/', data);
    invalidateScheduleCaches();
    return response.data;
  },

  update: async (id: number, data: Partial<KPI>) => {
    const response = await api.patch<KPI>(`/kpis/${id}/`, data);
    invalidateScheduleCaches();
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/kpis/${id}/`);
    invalidateScheduleCaches();
    return response;
  },

  getReadings: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<KPIValue[]>(`/kpis/${id}/readings/`, { params });
    return response.data;
  },

  addReading: async (id: number, data: Partial<KPIValue>) => {
    const response = await api.post<KPIValue>(`/kpis/${id}/add_reading/`, data);
    invalidateScheduleCaches();
    return response.data;
  },

  getTrend: async (id: number) => {
    const response = await api.get(`/kpis/${id}/trend/`);
    return response.data;
  },

  getSchedules: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<KPIReadingScheduleList[]>(`/kpis/${id}/schedules/`, { params });
    return response.data;
  },

  bulkCreate: async (kpis: Partial<KPI>[]) => {
    const response = await api.post<KPI[]>('/kpis/bulk_create/', { kpis });
    invalidateScheduleCaches();
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/kpis/${id}/deletion-impact/`),

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/kpis/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/kpis/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  resultsExport: async (params?: Record<string, any>) => {
    const response = await api.get('/kpis/results-export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/kpis/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    invalidateScheduleCaches();
    return response.data;
  },
};

// ============================================================
// KPI Values API
// ============================================================
export const kpiValuesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<KPIValue>>('/kpi-values/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPIValue>(`/kpi-values/${id}/`);
    return response.data;
  },

  create: async (data: Partial<KPIValue>) => {
    const response = await api.post<KPIValue>('/kpi-values/', data);
    invalidateScheduleCaches();
    return response.data;
  },

  update: async (id: number, data: Partial<KPIValue>) => {
    const response = await api.patch<KPIValue>(`/kpi-values/${id}/`, data);
    invalidateScheduleCaches();
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/kpi-values/${id}/`);
    invalidateScheduleCaches();
    return response;
  },
};

// ============================================================
// Schedules API
// ============================================================
export const schedulesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<KPIReadingScheduleList>>('/schedules/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPIReadingSchedule>(`/schedules/${id}/`);
    return response.data;
  },

  markComplete: async (id: number, value?: number, notes?: string) => {
    const response = await api.post<KPIReadingSchedule>(`/schedules/${id}/mark_complete/`, { value, notes });
    invalidateScheduleCaches();
    return response.data;
  },

  generate: async (kpiId: number, year?: number) => {
    const response = await api.post('/schedules/generate/', { kpi_id: kpiId, year });
    invalidateScheduleCaches();
    return response.data;
  },

  generateByOrg: async (organizationId: number, year?: number) => {
    const response = await api.post('/schedules/generate_by_org/', { organization_id: organizationId, year });
    invalidateScheduleCaches();
    return response.data;
  },

  orgSummary: async (params?: Record<string, any>) => {
    const response = await api.get('/schedules/org-summary/', { params });
    return response.data;
  },

  dueReadings: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<DueKPIReading>>('/schedules/due-readings/', { params });
    return response.data;
  },
};

// ============================================================
// Notifications API
// ============================================================
export const notificationsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<NotificationList>>('/notifications/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Notification>(`/notifications/${id}/`);
    return response.data;
  },

  markRead: async (id: number) => {
    const response = await api.post<Notification>(`/notifications/${id}/mark_read/`);
    return response.data;
  },

  markAllRead: async () => {
    const response = await api.post<{ marked_read: number }>('/notifications/mark_all_read/');
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get<{ unread_count: number }>('/notifications/unread_count/');
    return response.data;
  },
};

// ============================================================
// Users API
// ============================================================
export const usersApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<User>>('/users/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<User>(`/users/${id}/`);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get<User>('/users/me/');
    return response.data;
  },

  create: async (data: {
    username: string;
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    profile?: {
      user_type: 'superuser' | 'strategy_team' | 'org_employee' | 'executive';
      organization?: number | null;
      phone?: string;
      job_title?: string;
    };
  }) => {
    const response = await api.post<User>('/users/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<User & { password?: string; profile?: Partial<User['profile']> }>) => {
    const response = await api.patch<User>(`/users/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/users/${id}/`);
    return response;
  },

  updateProfile: async (data: Partial<User['profile']>) => {
    const response = await api.patch<User>('/users/update_profile/', data);
    return response.data;
  },
};

// ============================================================
// Dashboard API
// ============================================================
export const dashboardApi = {
  getData: async (year?: number) => {
    const params = year ? { year } : undefined;
    const response = await api.get<DashboardData>('/dashboard/', { params });
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get<DashboardSettings>('/dashboard-settings/my_settings/');
    return response.data;
  },

  saveSettings: async (data: Partial<DashboardSettings>) => {
    const response = await api.post<DashboardSettings>('/dashboard-settings/save_settings/', data);
    return response.data;
  },

  getPerformance: async (orgId?: number) => {
    const url = orgId ? `/performance/${orgId}/` : '/performance/';
    const response = await api.get<PerformanceData>(url);
    return response.data;
  },
};

// ============================================================
// Services API
// ============================================================
export const servicesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ServiceList>>('/services/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Service>(`/services/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Service>) => {
    const response = await api.post<Service>('/services/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Service>) => {
    const response = await api.patch<Service>(`/services/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/services/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/services/${id}/deletion-impact/`),

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/services/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/services/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  resultsExport: async (params?: Record<string, any>) => {
    const response = await api.get('/services/results-export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/services/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================================
// Organizational Units API
// ============================================================
export const organizationalUnitsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<OrganizationalUnitList>>('/organizational-units/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<OrganizationalUnit>(`/organizational-units/${id}/`);
    return response.data;
  },

  create: async (data: Partial<OrganizationalUnit>) => {
    const response = await api.post<OrganizationalUnit>('/organizational-units/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<OrganizationalUnit>) => {
    const response = await api.patch<OrganizationalUnit>(`/organizational-units/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/organizational-units/${id}/`);
    return response;
  },

  // Get strategic goals this unit is involved in
  strategicGoals: async (id: number) => {
    const response = await api.get<StrategicGoalList[]>(`/organizational-units/${id}/strategic-goals/`);
    return response.data;
  },

  // Get organizational units as hierarchical tree
  tree: async (params?: Record<string, any>) => {
    const response = await api.get<{ tree: any[]; primary_color: string; secondary_color: string }>('/organizational-units/tree/', { params });
    return response.data;
  },

  // Move a unit to a new parent with sibling reordering
  move: async (id: number, data: { new_parent_id: number | null; position: number }) => {
    const response = await api.post<{ tree: any[]; primary_color: string; secondary_color: string }>(`/organizational-units/${id}/move/`, data);
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/organizational-units/${id}/deletion-impact/`),
};

// ============================================================
// Employees API (الموظفين)
// ============================================================
export const employeesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<Employee>>('/employees/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Employee>(`/employees/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Employee>) => {
    const response = await api.post<Employee>('/employees/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Employee>) => {
    const response = await api.patch<Employee>(`/employees/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/employees/${id}/`);
    return response;
  },

  byUnit: async (unitId: number) => {
    const response = await api.get<PaginatedResponse<Employee>>(`/employees/by-unit/${unitId}/`);
    return response.data;
  },

  uploadImage: async (id: number, formData: FormData) => {
    const response = await api.patch<Employee>(`/employees/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get processes where employee is assigned to steps (via ManyToMany)
  processes: async (id: number) => {
    const response = await api.get<{
      employee: { id: number; name: string; position: string };
      processes: ProcessList[];
      total_processes: number;
    }>(`/employees/${id}/processes/`);
    return response.data;
  },

  // Import employees from Excel file
  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/employees/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/employees/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/employees/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  resultsExport: async (params?: Record<string, any>) => {
    const response = await api.get('/employees/results-export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },
};

// ============================================================
// Unit Goals API (أهداف الوحدات التنظيمية)
// ============================================================
export const unitGoalsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<UnitGoalList>>('/unit-goals/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<UnitGoal>(`/unit-goals/${id}/`);
    return response.data;
  },

  create: async (data: Partial<UnitGoal>) => {
    const response = await api.post<UnitGoal>('/unit-goals/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<UnitGoal>) => {
    const response = await api.patch<UnitGoal>(`/unit-goals/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/unit-goals/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/unit-goals/${id}/deletion-impact/`),

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/unit-goals/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/unit-goals/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/unit-goals/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  resultsExport: async (params?: Record<string, any>) => {
    const response = await api.get('/unit-goals/results-export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },
};

// ============================================================
// Processes API (العمليات)
// ============================================================
export const processesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ProcessList>>('/processes/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Process>(`/processes/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Process>) => {
    const response = await api.post<Process>('/processes/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Process>) => {
    const response = await api.patch<Process>(`/processes/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/processes/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/processes/${id}/deletion-impact/`),

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/processes/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/processes/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  resultsExport: async (params?: Record<string, any>) => {
    const response = await api.get('/processes/results-export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/processes/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================================
// SubProcesses API (العمليات الفرعية)
// ============================================================
export const subProcessesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<SubProcess>>('/sub-processes/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<SubProcess>(`/sub-processes/${id}/`);
    return response.data;
  },

  create: async (data: Partial<SubProcess> | FormData) => {
    // When data is FormData, let axios set Content-Type automatically with boundary
    const response = await api.post<SubProcess>('/sub-processes/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<SubProcess> | FormData) => {
    // When data is FormData, let axios set Content-Type automatically with boundary
    const response = await api.patch<SubProcess>(`/sub-processes/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/sub-processes/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/sub-processes/${id}/deletion-impact/`),
};

// ============================================================
// Process Steps API (خطوات العملية)
// ============================================================
export const processStepsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ProcessStep>>('/process-steps/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<ProcessStep>(`/process-steps/${id}/`);
    return response.data;
  },

  create: async (data: Partial<ProcessStep>) => {
    const response = await api.post<ProcessStep>('/process-steps/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<ProcessStep>) => {
    const response = await api.patch<ProcessStep>(`/process-steps/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/process-steps/${id}/`);
    return response;
  },
};

// ============================================================
// Risks API
// ============================================================
export const risksApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<any>>('/risks/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<any>(`/risks/${id}/`);
    return response.data;
  },

  create: async (data: Partial<any>) => {
    const response = await api.post<any>('/risks/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<any>) => {
    const response = await api.patch<any>(`/risks/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/risks/${id}/`);
    return response;
  },

  forItem: async (relatedType: string, relatedId: number) => {
    const response = await api.get<any[]>(`/risks/for-item/${relatedType}/${relatedId}/`);
    return response.data;
  },

  statistics: async () => {
    const response = await api.get<any>('/risks/statistics/');
    return response.data;
  },

  downloadTemplate: async (lang: string = 'ar', departmentId?: number | null) => {
    const response = await api.get('/risks/download-template/', {
      responseType: 'blob',
      params: departmentId ? { lang, department_id: departmentId } : { lang },
    });
    return response;
  },

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/risks/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  exportExcel: async (params?: Record<string, any>) => {
    const response = await api.get('/risks/export/', {
      responseType: 'blob',
      params,
    });
    return response;
  },
};

// ============================================================
// Evidence API
// ============================================================
export interface EvidenceUploader {
  id: number;
  username: string;
  full_name: string;
}

export interface Evidence {
  id: number;
  related_type: string;
  related_id: number;
  evidence_type: 'image' | 'file' | 'text' | 'link';
  title: string;
  description?: string;
  target_criterion?: string;
  impact?: string;
  file?: string | null;
  text_content?: string;
  external_link?: string;
  organization_id?: number | null;
  organization_name?: string | null;
  uploaded_by_id?: number;
  uploaded_by_details?: EvidenceUploader | null;
  uploaded_at?: string;
}

export const evidenceApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<Evidence>>('/evidence/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Evidence>(`/evidence/${id}/`);
    return response.data;
  },

  create: async (data: FormData) => {
    const response = await api.post<Evidence>('/evidence/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  update: async (id: number, data: FormData) => {
    const response = await api.patch<Evidence>(`/evidence/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  exportZip: async (params?: Record<string, any>) => {
    const response = await api.get(`/evidence/export-zip/`, {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  forObject: async (type: string, id: number) => {
    const response = await api.get<Evidence[]>(`/evidence/for-object/`, {
      params: { type, id }
    });
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/evidence/${id}/`);
    return response;
  },
};

// ============================================================
// Change Requests API (طلبات التعديل/الحذف)
// ============================================================
export const changeRequestsApi = {
  list: (params?: Record<string, any>) =>
    api.get('/change-requests/', { params }),
  get: (id: number) =>
    api.get(`/change-requests/${id}/`),
  approve: (id: number) =>
    api.post(`/change-requests/${id}/approve/`),
  reject: (id: number, reason: string) =>
    api.post(`/change-requests/${id}/reject/`, { reason }),
  cancel: (id: number) =>
    api.post(`/change-requests/${id}/cancel/`),
  bulkApprove: (ids: number[]) =>
    api.post('/change-requests/bulk-approve/', { ids }),
  bulkReject: (ids: number[], reason: string) =>
    api.post('/change-requests/bulk-reject/', { ids, reason }),
};

// ============================================================
// Global Search API (البحث العام)
// ============================================================
export interface SearchResults {
  query: string;
  total_count: number;
  results: {
    strategic_goals: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
      }>;
    };
    initiatives: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        strategic_goal__name: string;
      }>;
    };
    activities: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        initiative__name: string;
      }>;
    };
    kpis: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        source_type: string;
      }>;
    };
    processes: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
      }>;
    };
    sub_processes: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        process__name: string;
      }>;
    };
    services: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
      }>;
    };
    unit_goals: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        unit__name: string;
      }>;
    };
    risks: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        level: string;
      }>;
    };
  };
}

export const searchApi = {
  search: async (query: string) => {
    const response = await api.get<SearchResults>('/search/', {
      params: { q: query }
    });
    return response.data;
  },
};
